import {
    CrawlItem,
    CrawlItemKind,
    CrawlItemSort,
    CrawlOutcome,
    CrawlRun
} from "../../../common/interfaces/admin/crawl.interface";
import { MenuItem } from "../../../common/interfaces/comps/menu-item.interface";

/** Outcome chip order and labels (web_scraper_provider_design.md §10). */
export const OUTCOME_LABELS: { outcome: CrawlOutcome; label: string }[] = [
    { outcome: "new", label: "New" },
    { outcome: "modified", label: "Modified" },
    { outcome: "renamed", label: "Renamed" },
    { outcome: "unchanged", label: "Unchanged" },
    { outcome: "removed", label: "Removed" },
    { outcome: "skipped", label: "Skipped" },
    { outcome: "failed", label: "Failed" }
];

export const KIND_LABELS: { kind: CrawlItemKind; label: string }[] = [
    { kind: "page", label: "Page" },
    { kind: "document", label: "Document" },
    { kind: "media", label: "Media" },
    { kind: "other", label: "Other" }
];

export const DECISION_LABELS: { decision: "include" | "exclude"; label: string }[] = [
    { decision: "include", label: "Included" },
    { decision: "exclude", label: "Excluded" }
];

/** Sort menu entries, in menu order, and the query each one sends. */
export const CRAWL_SORTS: Record<string, { sortBy: CrawlItemSort; sortOrder: "asc" | "desc" }> = {
    "A-Z Title": { sortBy: "title", sortOrder: "asc" },
    "Z-A Title": { sortBy: "title", sortOrder: "desc" },
    "A-Z Address": { sortBy: "canonicalUrl", sortOrder: "asc" },
    "Z-A Address": { sortBy: "canonicalUrl", sortOrder: "desc" },
    "Size Ascending": { sortBy: "sizeBytes", sortOrder: "asc" },
    "Size Descending": { sortBy: "sizeBytes", sortOrder: "desc" }
};
export const CRAWL_SORT_LIST = Object.keys(CRAWL_SORTS);
export const DEFAULT_CRAWL_SORT = "A-Z Title";

/** The comma list a checkbox filter sends, or undefined when nothing is checked. */
export const filterParam = <T extends string>(checked: MenuItem<T>[]): string | undefined =>
    checked.length ? checked.map(item => item.value).join(",") : undefined;

/** Plain-words wording for the §5.4 reason buckets (BR-37); keys are the crawler's ItemReason values. */
export const REASON_LABELS: Record<string, string> = {
    outside_boundary: "Outside the site boundary",
    excluded_by_pattern: "Excluded by a pattern",
    not_included: "Not matched by the include pattern",
    blocked_by_robots: "Blocked by robots.txt",
    robots_unavailable: "robots.txt could not be read",
    noindex: "Marked noindex",
    needs_authentication: "Needs authentication",
    bot_challenge: "Blocked by a bot challenge",
    not_found: "Not found (404)",
    soft_404: "Looks like a missing page",
    throttled: "Rate limited by the site",
    transient_failure: "Temporary failure",
    needs_javascript: "Needs JavaScript to render",
    empty: "No readable content",
    too_large: "Too large",
    type_mismatch: "Served a different type than linked",
    duplicate: "Duplicate of another page",
    redirect_out_of_boundary: "Redirects outside the site boundary",
    too_many_redirects: "Too many redirects",
    unsupported_type: "Unsupported file type",
    google_workspace: "Google Workspace link (not supported)",
    embedded_media: "Embedded media (not supported)",
    media_disabled: "Audio and video are off for this site",
    documents_disabled: "Linked documents are off for this site",
    depth_limit: "Beyond the depth limit",
    page_cap: "Page limit reached",
    document_cap: "Document limit reached",
    bytes_budget: "Crawl size limit reached",
    time_budget: "Crawl time limit reached",
    blocked_address: "Blocked address",
    invalid_url: "Invalid URL",
    fetch_error: "Could not be fetched",
    not_linked: "No longer linked or listed",
    stage2_cap: "Download limit reached; checked on the next crawl"
};

export const reasonLabel = (reason?: string | null): string => {
    if (!reason) return "";
    return REASON_LABELS[reason] ?? reason.replace(/_/g, " ");
};

/** The reason plus the HTTP status for a failed fetch, e.g. "transient failure (HTTP 504)". */
export const failureLabel = (
    item: Pick<CrawlItem, "outcome" | "reason" | "httpStatus">
): string => {
    const label = reasonLabel(item.reason);
    if (item.outcome !== "failed" || !item.httpStatus) return label;
    return label ? `${label} (HTTP ${item.httpStatus})` : `HTTP ${item.httpStatus}`;
};

export const formatBytes = (bytes?: number | null): string => {
    if (bytes === null || bytes === undefined) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

export const formatDate = (value?: string | null): string => {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
};

export const itemSize = (item: CrawlItem): string =>
    item.kind === "page"
        ? item.wordCount != null
            ? `${item.wordCount} words`
            : ""
        : formatBytes(item.sizeBytes);

export const kindLabel = (kind: CrawlItem["kind"]): string =>
    kind === "page"
        ? "Page"
        : kind === "document"
          ? "Document"
          : kind === "media"
            ? "Media"
            : "Other";

/** D8: why a run is held for review, in plain words. */
export const reviewReasonLabel = (reason: string): string =>
    reason === "first_run"
        ? "first crawl of this site"
        : reason === "policy"
          ? "review policy"
          : reason === "removals_threshold"
            ? "many pages disappeared"
            : reason === "incomplete"
              ? "the crawl did not finish"
              : reason === "single_page"
                ? "single page re-fetch"
                : reason.replace(/_/g, " ");

/**
 * Why the crawl did not finish, in plain words, from the crawler's incomplete
 * reasons (limits, or a start / sitemap / hub page that could not be fetched).
 * The limit reasons carry the number of addresses left unvisited when the
 * run's counts have it.
 */
export const incompleteReasonLabel = (reason: string, run?: Pick<CrawlRun, "counts">): string => {
    const [code, ...rest] = reason.split(":");
    const target = rest.join(":");
    const skipped = run?.counts?.skippedByReason?.[code];
    const left = skipped
        ? ` (${skipped} ${skipped === 1 ? "address" : "addresses"} not visited)`
        : "";
    switch (code) {
        case "time_budget":
            return `the crawl time limit was reached${left}`;
        case "page_cap":
            return `the page limit was reached${left}`;
        case "document_cap":
            return `the document limit was reached${left}`;
        case "bytes_budget":
            return `the crawl size limit was reached${left}`;
        case "seed_failed":
            return `the start page could not be fetched (${target})`;
        case "sitemap_page_failed":
            return `a page listed in the sitemap could not be fetched (${target})`;
        case "hub_failed":
            return `a page with many links could not be fetched (${target})`;
        case "robots_unavailable":
            return `robots.txt could not be read for ${target}`;
        default:
            return reason.replace(/_/g, " ");
    }
};

export const incompleteSummary = (run: Pick<CrawlRun, "counts" | "incompleteReasons">): string =>
    (run.incompleteReasons ?? []).map(reason => incompleteReasonLabel(reason, run)).join("; ");

export const runSummary = (run: CrawlRun): string => {
    const c = run.counts || {};
    const parts = [
        `${c.new ?? 0} new`,
        `${c.modified ?? 0} modified`,
        `${c.removed ?? 0} removed`,
        `${c.unchanged ?? 0} unchanged`,
        `${c.skipped ?? 0} skipped`,
        `${c.failed ?? 0} failed`
    ];
    return parts.join(", ");
};

/** The CSP meta prepended to a snapshot so nothing in it can load or run. */
export const PREVIEW_CSP =
    "<meta http-equiv=\"Content-Security-Policy\" content=\"default-src 'none'; style-src 'unsafe-inline'; img-src data:;\">";

export const previewDocument = (html: string): string => {
    const base = '<base target="_blank">';
    if (/<head\b[^>]*>/i.test(html)) {
        return html.replace(/<head\b[^>]*>/i, match => `${match}${PREVIEW_CSP}${base}`);
    }
    return `<!doctype html><html><head>${PREVIEW_CSP}${base}</head><body>${html}</body></html>`;
};
